// TEMPORARY — minting /portal request codes from this project.
//
// /portal is where a request code belongs: it owns the counter, the format and
// every other kind of request that shares the namespace. Until it raises these
// bookings itself, the code has to be invented here, so this file is a copy of
// the parts of /portal that decide what one looks like:
//
//   - the Luhn mod-N check character, from `src/util/util_luhn.ts` there,
//     verbatim;
//   - the `O`/`I` folding of `normaliseForLuhn`, from
//     `src/pages/Agencies/util_agencies.ts` — which lives in
//     `normaliseShortCode` here, since it is a property of the stored codes
//     rather than of this file, and stays when this one goes;
//   - the year/month pair, from `getTwoCharacterDate` in
//     `src/pages/Requests/RequestsCrud/util_db_tourrequests.ts`;
//   - the assembly and the retry loop, from `generateLuhnCode` in
//     `TourRequestCrud.tsx` and `makeExploreRequestCode` in
//     `src/pages/ExploreBookings/util_explore_createtours.ts`.
//
// Copied rather than shared because the two projects deploy independently and
// have no package between them. It is a copy of a format, not of a decision:
// /portal remains free to change how it mints its own codes, and the day it
// mints ours the whole of this goes.
//
// **To remove it**, delete this file and its call sites, each marked TEMPORARY:
//   - `mintRequestCode` in `createBookingRequest` (functions/), and the
//     `portalRequestCode` line in `common` that calls it;
//   - in `PageBookingRequestList`: `generateReference`, the Generate button, the
//     `bookingReferenceHint` beside it, and the `usePartners` subscription,
//     which nothing else on that screen needs.
// Nothing else imports it, and nothing reads a request code back apart from a
// person. `portalRequestCode` on the booking stays: it is the field /portal will
// write into, and the reference dialog stays with it — a code still has to be
// correctable by hand.

import { normaliseShortCode, shortCodeLength } from './types_code';

/**
 * The alphabet a request code is spelled in — /portal's, exactly. 34 characters:
 * `I` and `O` are absent, because a code is read off a screen and typed into
 * another one, and neither is worth confusing with `1` and `0`.
 *
 * Its length is the modulus of the check-character arithmetic below, so this is
 * not a list that can be added to on one side of the pairing alone.
 */
export const luhnCodePoints = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/** The first character of every code minted here — Eighty Days Experiences. */
export const requestCodeFirstLetter = 'X';

/**
 * The character standing for who raised the request.
 *
 * A constant rather than the signed-in admin's own letter, because nobody at
 * Eighty Days raises these: they arrive from a partner's concierge, and there is
 * no /portal user behind them to name. 'J' is the letter /portal has set aside
 * for requests that came in this way.
 */
export const requestCodeUserChar = 'J';

/** How many random codes are tried before giving up on an unused one. */
const maxCodeAttempts = 30;

// --- Luhn mod N, from /portal's src/util/util_luhn.ts ---
// https://en.wikipedia.org/wiki/Luhn_mod_N_algorithm#Algorithm_in_JavaScript

function codePointFromCharacter(character: string): number {
  return luhnCodePoints.indexOf(character);
}

function characterFromCodePoint(codePoint: number): string {
  return luhnCodePoints.charAt(codePoint);
}

export function generateCheckCharacter(input: string): string {
  let factor = 2;
  let sum = 0;
  const n = luhnCodePoints.length;

  // Starting from the right and working leftwards is easier since
  // the initial "factor" will always be "2".
  for (let i = input.length - 1; i >= 0; i--) {
    const codePoint = codePointFromCharacter(input.charAt(i));
    let addend = factor * codePoint;

    // Alternate the "factor" that each "codePoint" is multiplied by
    factor = (factor === 2) ? 1 : 2;

    // Sum the digits of the "addend" as expressed in base "n"
    addend = (Math.floor(addend / n)) + (addend % n);
    sum += addend;
  }

  // Calculate the number that must be added to the "sum"
  // to make it divisible by "n".
  const remainder = sum % n;
  const checkCodePoint = (n - remainder) % n;
  return characterFromCodePoint(checkCodePoint);
}

// --- The code itself ---

/**
 * The two characters standing for when a request came in: the last digit of the
 * year, then the month as one character.
 *
 * October, November and December are `X`, `Y` and `Z` — the alphabet has no
 * two-digit anything, and a month is the one segment that has to fit in one
 * character to leave room for the rest.
 *
 * Null outside 2020–2040, where the single year digit stops being unambiguous
 * enough to be worth writing down. /portal draws the same line.
 */
export function requestCodeDateChars(dateIso: string): string | null {
  const date = new Date(dateIso);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getUTCFullYear();
  if (year < 2020 || year > 2040) return null;

  const month = date.getUTCMonth() + 1;
  const monthChar = month === 10 ? 'X' : month === 11 ? 'Y' : month === 12 ? 'Z' : String(month);
  return `${String(year).slice(-1)}${monthChar}`;
}

/**
 * A stored two-character code as a request code can carry it, or null where it
 * cannot carry it at all.
 *
 * `normaliseShortCode` has already folded `O` and `I` onto `0` and `1` — the
 * same thing /portal does to an agency's mnemonic, and the reason a stored code
 * is always spellable here. What is left to refuse is a code of the wrong length
 * (an experience that has not been given one) or one holding a character the
 * alphabet has no room for.
 */
function luhnSegment(code: string): string | null {
  const normalised = normaliseShortCode(code);
  if (normalised.length !== shortCodeLength) return null;
  if ([...normalised].some((c) => !luhnCodePoints.includes(c))) return null;
  return normalised;
}

/** What a code is built from. Everything else about it is fixed. */
export interface RequestCodePartsType {
  /** When the request came in, ISO. Gives the code its year and month. */
  dateIso: string;
  /** `ExperienceType.code`. */
  experienceCode: string;
  /** `PartnerType.code`. */
  partnerCode: string;
}

/**
 * One candidate code, laid out the way /portal lays out an Explore code:
 * `X68E-UKAMJ6`.
 *
 *   `X`  this project
 *   `6`  last digit of the year the request came in
 *   `8`  its month
 *   `E`  the check character
 *   `UK` the experience
 *   `AM` the partner
 *   `J`  raised by a partner's concierge — see `requestCodeUserChar`
 *   `6`  a random character, which is the only thing making two requests for the
 *        same experience by the same partner in the same month differ
 *
 * The check character is computed over the other nine in reading order and then
 * moved to the fourth position, before the hyphen — /portal's arrangement, so
 * that the half of the code before the hyphen is self-checking to look at.
 *
 * Null where a segment is missing or unusable, which is a thing to report rather
 * than to work around: an experience or partner with no two-character code
 * cannot be named in a request code at all.
 */
function buildRequestCode(parts: RequestCodePartsType, randomChar: string): string | null {
  const dateChars = requestCodeDateChars(parts.dateIso);
  const experience = luhnSegment(parts.experienceCode);
  const partner = luhnSegment(parts.partnerCode);
  if (!dateChars || !experience || !partner) return null;

  const tail = `${experience}${partner}${requestCodeUserChar}${randomChar}`;
  const check = generateCheckCharacter(`${requestCodeFirstLetter}${dateChars}${tail}`);
  return `${requestCodeFirstLetter}${dateChars}${check}-${tail}`;
}

/** Why a code could not be minted, in words for whoever pressed the button. */
export function requestCodeProblem(parts: RequestCodePartsType): string | null {
  if (!requestCodeDateChars(parts.dateIso)) {
    return 'A request code can only be made for a request received between 2020 and 2040.';
  }
  if (!luhnSegment(parts.experienceCode)) {
    return 'This experience has no two-character code yet. Set one on the experience first.';
  }
  if (!luhnSegment(parts.partnerCode)) {
    return 'This partner has no two-character code yet. Set one on the partner first.';
  }
  return null;
}

export type RequestCodeResultType =
  | { ok: true; code: string }
  | { ok: false; error: string };

/**
 * Mints a code that nothing else is using.
 *
 * `isTaken` is the caller's, because the two callers can answer it at very
 * different cost: the CMS already holds every booking request in memory and
 * answers from the array, while the Cloud Function asks Firestore. Neither can
 * see /portal's own requests, and neither needs to — `X` is this project's
 * first letter and nothing over there mints one.
 *
 * Only the last character varies between attempts, so there are 34 codes for a
 * given experience, partner and month and this gives up after
 * `maxCodeAttempts` of them. Both numbers are /portal's. Running out is a real
 * answer rather than an error to swallow: it means that partner has booked that
 * experience 34 times this month, and the desk should hear about it.
 */
export async function generateRequestCode(
  parts: RequestCodePartsType,
  isTaken: (code: string) => boolean | Promise<boolean>,
): Promise<RequestCodeResultType> {
  const problem = requestCodeProblem(parts);
  if (problem) return { ok: false, error: problem };

  for (let attempt = 0; attempt < maxCodeAttempts; attempt++) {
    const randomChar = luhnCodePoints.charAt(Math.floor(Math.random() * luhnCodePoints.length));
    const code = buildRequestCode(parts, randomChar);
    // `requestCodeProblem` above has already established that the segments are
    // usable, so this cannot be null — but the check is what makes that true
    // rather than assumed.
    if (!code) return { ok: false, error: 'That request code could not be built.' };
    if (!(await isTaken(code))) return { ok: true, code };
  }

  return { ok: false, error: `Could not find an unused code after ${maxCodeAttempts} tries.` };
}
